import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Search, Filter, RefreshCw, Send, AlertCircle, PauseCircle,
  CheckCircle2, ExternalLink, Calendar, X,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';

/**
 * Communication Center — admin-only cross-proposal email audit log.
 * Lives on top of the `email_logs` Mongo collection via GET /api/email-logs.
 * Lets admins answer questions like "Have we emailed this customer in the
 * last 7 days?" or "Which proposals had failed payment confirmations?"
 */

const STATUS_META = {
  sent: { label: 'Delivered', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  failed: { label: 'Failed', color: '#dc2626', bg: '#fee2e2', icon: AlertCircle },
  paused: { label: 'Paused', color: '#b45309', bg: '#fef3c7', icon: PauseCircle },
};

const EMAIL_TYPES = [
  { value: '', label: 'All types' },
  { value: 'proposal_email', label: 'Proposal sent' },
  { value: 'followup_day1', label: 'Follow-up · Day 1' },
  { value: 'followup_day3', label: 'Follow-up · Day 3' },
  { value: 'followup_day5', label: 'Follow-up · Day 5' },
  { value: 'urgent_reminder', label: 'Urgent reminder' },
  { value: 'payment_confirmation', label: 'Payment confirmation' },
  { value: 'pickup_ready', label: 'Pickup ready' },
  { value: 'order_status', label: 'Order status' },
  { value: 'production_update', label: 'Production update' },
  { value: 'lead_autoresponder', label: 'Lead auto-responder' },
];

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoMinusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const CommunicationCenter = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ sent: 0, failed: 0, paused: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    emailType: '',
    startDate: '',
    endDate: '',
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.emailType) params.email_type = filters.emailType;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      params.limit = 200;
      const { data } = await axios.get('/email-logs', { params });
      setLogs(data.logs || []);
      setStats(data.stats || { sent: 0, failed: 0, paused: 0 });
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const applyDateChip = (days) => {
    setFilters((f) => ({ ...f, startDate: isoMinusDays(days), endDate: todayISO() }));
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', emailType: '', startDate: '', endDate: '' });
  };

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v && String(v).length > 0),
    [filters],
  );

  const handleResend = async (log) => {
    if (resendingId) return;
    if (!log.proposal_id) {
      toast.error('This log row has no proposal — cannot resend.');
      return;
    }
    const confirmed = window.confirm(`Resend "${log.label || log.email_type}" to ${log.to_email}?`);
    if (!confirmed) return;
    setResendingId(log.id);
    try {
      const { data } = await axios.post(
        `/proposals/${log.proposal_id}/email-log/${log.id}/resend`,
      );
      if (data.success) {
        toast.success(`Resent ${log.label} → ${data.to}`);
      } else {
        toast.warning('Send returned no success — check kill switch / SMTP credentials.');
      }
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }} data-testid="communication-center">
      <AppHeader />
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 24px' }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Mail size={28} color="#0ea5e9" />
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
              Communication Center
            </h1>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
              Every email sent across every proposal — sortable, searchable, resendable.
            </div>
          </div>
        </div>

        {/* Stats summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <StatCard label="Total in view" value={total} accent="#0f172a" data-testid="stat-total" />
          <StatCard label="Delivered" value={stats.sent} accent="#16a34a" data-testid="stat-sent" />
          <StatCard label="Failed" value={stats.failed} accent="#dc2626" data-testid="stat-failed" />
          <StatCard label="Paused" value={stats.paused} accent="#b45309" data-testid="stat-paused" />
        </div>

        {/* Filter bar */}
        <div
          style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#475569' }}>
            <Filter size={16} />
            <span style={{ fontWeight: 600 }}>Filters</span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters"
                style={{
                  marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
                  background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: 13,
                }}
              >
                <X size={14} /> Clear all
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
              />
              <input
                type="text"
                placeholder="Search by recipient, subject, or sender…"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                data-testid="filter-search"
                style={{
                  width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #cbd5e1', borderRadius: 8,
                  fontSize: 13,
                }}
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              data-testid="filter-status"
              style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            >
              <option value="">All statuses</option>
              <option value="sent">Delivered</option>
              <option value="failed">Failed</option>
              <option value="paused">Paused</option>
            </select>
            <select
              value={filters.emailType}
              onChange={(e) => setFilters((f) => ({ ...f, emailType: e.target.value }))}
              data-testid="filter-type"
              style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            >
              {EMAIL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              data-testid="filter-start-date"
              style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              data-testid="filter-end-date"
              style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <DateChip label="Today" onClick={() => applyDateChip(0)} testid="chip-today" />
            <DateChip label="Last 7 days" onClick={() => applyDateChip(7)} testid="chip-7d" />
            <DateChip label="Last 30 days" onClick={() => applyDateChip(30)} testid="chip-30d" />
            <DateChip label="Last 90 days" onClick={() => applyDateChip(90)} testid="chip-90d" />
            <button
              onClick={fetchLogs}
              data-testid="refresh-logs"
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
                background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
              }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Results table */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          {loading && logs.length === 0 && (
            <div style={{ padding: 32, color: '#64748b', textAlign: 'center' }}>Loading…</div>
          )}
          {!loading && logs.length === 0 && (
            <div style={{ padding: 32, color: '#64748b', textAlign: 'center' }} data-testid="empty-state">
              <Mail size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontWeight: 600, color: '#0f172a' }}>No emails match your filters</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>
                Try widening the date range or clearing filters above.
              </div>
            </div>
          )}
          {logs.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="logs-table">
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#475569' }}>
                  <th style={th}>When</th>
                  <th style={th}>Type</th>
                  <th style={th}>Recipient</th>
                  <th style={th}>Customer / Proposal</th>
                  <th style={th}>Subject</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const meta = STATUS_META[log.status] || { label: log.status, color: '#475569', bg: '#f1f5f9', icon: Mail };
                  const Icon = meta.icon;
                  return (
                    <tr key={log.id} style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`row-${log.id}`}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                          <Calendar size={12} /> {formatTime(log.created_at)}
                        </div>
                        {log.triggered_by && (
                          <div style={{ marginTop: 2, fontSize: 11, color: '#94a3b8' }}>{log.triggered_by}</div>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{log.label || log.email_type}</span>
                      </td>
                      <td style={td}>
                        <div style={{ color: '#0f172a' }}>{log.to_email}</div>
                        {log.cc_emails && log.cc_emails.length > 0 && (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Cc: {log.cc_emails.join(', ')}</div>
                        )}
                      </td>
                      <td style={td}>
                        {log.proposal_id ? (
                          <Link
                            to={`/proposal/${log.proposal_id}`}
                            style={{ color: '#0ea5e9', textDecoration: 'none', fontWeight: 600 }}
                            data-testid={`open-proposal-${log.id}`}
                          >
                            {log.proposal_number || log.proposal_id.slice(0, 8)}
                          </Link>
                        ) : <span style={{ color: '#94a3b8' }}>—</span>}
                        {log.customer_name && (
                          <div style={{ fontSize: 12, color: '#64748b' }}>{log.customer_name}</div>
                        )}
                      </td>
                      <td style={{ ...td, maxWidth: 320, color: '#475569' }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.subject}
                        </div>
                        {log.error && (
                          <div style={{ marginTop: 2, fontSize: 11, color: '#b91c1c' }}>{log.error}</div>
                        )}
                      </td>
                      <td style={td}>
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 999, background: meta.bg, color: meta.color,
                          }}
                          data-testid={`status-${log.id}`}
                        >
                          <Icon size={12} /> {meta.label}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {log.proposal_id && (
                            <button
                              onClick={() => navigate(`/proposal/${log.proposal_id}`)}
                              data-testid={`view-${log.id}`}
                              title="Open proposal"
                              style={iconBtn}
                            >
                              <ExternalLink size={14} />
                            </button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResend(log)}
                            disabled={resendingId === log.id || !log.proposal_id}
                            data-testid={`resend-${log.id}`}
                          >
                            <Send size={12} style={{ marginRight: 4 }} />
                            {resendingId === log.id ? '…' : 'Resend'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {total > logs.length && (
          <div style={{ marginTop: 12, color: '#64748b', fontSize: 12, textAlign: 'center' }}>
            Showing {logs.length} of {total} — narrow your filters to see more relevant rows.
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent, ...rest }) => (
  <div
    {...rest}
    style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px',
    }}
  >
    <div style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>{label}</div>
    <div style={{ marginTop: 4, color: accent, fontSize: 24, fontWeight: 700 }}>{value}</div>
  </div>
);

const DateChip = ({ label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    style={{
      padding: '4px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999,
      fontSize: 12, color: '#475569', cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

const th = { padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '12px 14px', verticalAlign: 'top' };
const iconBtn = {
  background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: 6, cursor: 'pointer',
  color: '#475569', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export default CommunicationCenter;
